/**
 * "Reseller Applications" — the second tab of CRM ▸ Reseller Management
 * (`/crm/administration/resellers?tab=applications`): the queue of companies
 * asking to become XOP resellers (spec E §11.1). Moved here from System Admin
 * on 2026-09-12; the old `/admin/reseller-applications` URL redirects.
 *
 * A row is a `ResellerApplication` on crm-api: what the applicant typed,
 * which of the four documents exist, and the matcher's best guess at which
 * existing reseller this already is. NOTHING here grants access — the
 * decision happens on the review page, and until it does the applicant can
 * see exactly one screen on the reseller portal (the wall).
 *
 * Rendered ONLY for a holder of `reseller_applications:access` (the
 * dashboard's `crm:reseller_applications` grant, super_admin baseline) — the
 * page decides whether the tab exists at all; this component never asks.
 */
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  RESELLER_APPLICATION_FILTER_LABELS,
  RESELLER_APPLICATION_STATUS_FILTERS,
  RESELLER_APPLICATION_STATUS_LABELS,
  applicantName,
  countersignComplete,
  type ResellerApplication,
  type ResellerApplicationStatusFilter,
} from "@xop/dashboard-shared";
import { useState } from "react";
import type { JSX } from "react";
import { useNavigate } from "react-router-dom";

import { PageLoader } from "@/components/ui/page-loader";
import { ApiError } from "@/lib/api";
import { headCellSx } from "@/pages/admin/dashboard-users/tableStyles";
import { formatWhen } from "./format.js";
import { useResellerApplications } from "./useResellerApplications.js";

function signedComplete(a: ResellerApplication): boolean {
  return a.documents.signed_agreement.attached && a.documents.signed_nda.attached;
}

export function ResellerApplicationsTab(): JSX.Element {
  const [filter, setFilter] = useState<ResellerApplicationStatusFilter>("open");
  const navigate = useNavigate();
  const query = useResellerApplications(filter);
  const rows = query.data?.applications ?? [];

  return (
    <Box sx={{ display: "grid", gap: 3 }} data-cy="reseller-applications-tab">
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 2 }}>
        <TextField
          select
          size="small"
          label="Status"
          value={filter}
          onChange={(e) => setFilter(e.target.value as ResellerApplicationStatusFilter)}
          // Native so the filter IS a <select>: real keyboard behaviour, and
          // Cypress can `.select()` it without a listbox portal in the way.
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-cy": "reseller-applications-status" }}
          sx={{ minWidth: 220 }}
        >
          {RESELLER_APPLICATION_STATUS_FILTERS.map((f) => (
            <option key={f} value={f}>
              {RESELLER_APPLICATION_FILTER_LABELS[f]}
            </option>
          ))}
        </TextField>
        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
          An applicant sees only the onboarding wall until you activate them here.
        </Typography>
      </Box>

      {query.isLoading && <PageLoader />}

      {query.isError && (
        <Box
          role="alert"
          sx={{
            display: "grid",
            gap: 1,
            borderRadius: 1,
            border: 1,
            borderColor: (t) => alpha(t.palette.error.main, 0.4),
            bgcolor: (t) => alpha(t.palette.error.main, 0.12),
            px: 1.5,
            py: 1.5,
            fontSize: 14,
            color: "error.main",
          }}
        >
          <Box sx={{ fontWeight: 600 }}>Couldn&rsquo;t load reseller applications.</Box>
          <Box sx={{ fontSize: 12 }}>
            {query.error instanceof ApiError ? query.error.message : "Unknown error from /api/admin/reseller-applications."}
          </Box>
          <Box>
            <Button size="small" variant="text" color="inherit" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </Box>
        </Box>
      )}

      {query.data && (
        <Card>
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ "& td, & th": { fontSize: 14 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Company</TableCell>
                    <TableCell sx={headCellSx}>Contact</TableCell>
                    <TableCell sx={headCellSx}>Email</TableCell>
                    <TableCell sx={headCellSx}>Registered</TableCell>
                    <TableCell sx={headCellSx}>Status</TableCell>
                    <TableCell sx={headCellSx}>Documents</TableCell>
                    <TableCell sx={headCellSx}>Top candidate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((a) => (
                    <TableRow
                      key={a.id}
                      hover
                      role="button"
                      tabIndex={0}
                      aria-label={`Open application from ${a.company_name}`}
                      data-cy="reseller-application-row"
                      data-id={a.id}
                      onClick={() => navigate(`/crm/administration/reseller-applications/${a.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(`/crm/administration/reseller-applications/${a.id}`);
                        }
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Box sx={{ fontWeight: 500, color: "text.primary" }}>{a.company_name}</Box>
                        {a.website_domain && (
                          <Box sx={{ fontSize: 12, color: "text.disabled" }}>{a.website_domain}</Box>
                        )}
                      </TableCell>
                      <TableCell>{applicantName(a)}</TableCell>
                      <TableCell>{a.email}</TableCell>
                      <TableCell>{formatWhen(a.created_at)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={RESELLER_APPLICATION_STATUS_LABELS[a.status]}
                          color={a.status === "active" ? "success" : a.status === "rejected" ? "default" : "info"}
                          variant={a.status === "rejected" ? "outlined" : "filled"}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {/* Two facts, two chips: has the APPLICANT signed both,
                              has XOP countersigned both. The second is what
                              gates Activate on the review page. */}
                          <Chip
                            size="small"
                            label={signedComplete(a) ? "Signed" : "Awaiting signature"}
                            color={signedComplete(a) ? "success" : "default"}
                            variant={signedComplete(a) ? "filled" : "outlined"}
                          />
                          <Chip
                            size="small"
                            label={countersignComplete(a) ? "Countersigned" : "Awaiting countersign"}
                            color={countersignComplete(a) ? "success" : "default"}
                            variant={countersignComplete(a) ? "filled" : "outlined"}
                          />
                        </Box>
                      </TableCell>
                      <TableCell data-testid={`top-candidate-${a.id}`}>
                        {a.top_candidate ? (
                          <Box>
                            <Box sx={{ color: "text.primary" }}>{a.top_candidate.name}</Box>
                            <Box sx={{ fontSize: 12, color: "text.disabled" }}>
                              {a.top_candidate.code ? `${a.top_candidate.code} · ` : ""}
                              {Math.round(a.top_candidate.score * 100)}% match
                            </Box>
                          </Box>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{ py: 3, textAlign: "center", fontSize: 14, color: "text.disabled" }}
                        data-cy="reseller-applications-empty"
                      >
                        No {filter === "open" ? "open" : RESELLER_APPLICATION_FILTER_LABELS[filter].toLowerCase()} applications.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
